import { Component, ContentChild, Input, OnDestroy, OnInit } from '@angular/core'
import { FormArray, FormGroup } from '@angular/forms'
import * as lodash from 'lodash'
import { Group, DragulaService } from 'ng2-dragula'
import { debounceTime, pairwise, startWith } from 'rxjs/operators'
import { SignerFormDirective } from './signing-order-editor.directive'
import { Destroyable, untilDestroy } from '@tokeet-frontend/tv3-platform'

export const defaultSignerOrderValue = 1
let DraggableSignerItemsGroupIndex = 0
@Component({
  selector: 'app-signing-order-editor',
  templateUrl: './signing-order-editor.component.html',
  styleUrls: ['./signing-order-editor.component.scss'],
})
export class SigningOrderEditorComponent extends Destroyable implements OnInit, OnDestroy {
  @Input() itemsFormArray: FormArray
  @Input() labelKey: string
  @Input() sortable = true

  @ContentChild(SignerFormDirective) formDirective: SignerFormDirective

  draggableSignerItemsGroupKey = `DraggableSignerItemsGroup-${DraggableSignerItemsGroupIndex++}`
  draggableSignerItemHandler = 'sort-drag-handler'

  groupedSignerItemCtrls: FormGroup[][] = []
  dragulaGroup: Group

  get newOrderValue() {
    const total = this.groupedSignerItemCtrls.length
    return Math.max(total, defaultSignerOrderValue) // default to 1
  }

  isSignerDataChanged = new Date()
  getItemsValue(items: FormGroup[], toggle: Date): any[] {
    return lodash.map(items, (t) => t.getRawValue())
  }

  constructor(private dragulaService: DragulaService) {
    super()
  }

  ngOnInit(): void {
    this.dragulaGroup = this.dragulaService.createGroup(this.draggableSignerItemsGroupKey, {
      moves: (el, container, handle) => {
        return lodash.includes(lodash.split(handle.className, ' '), this.draggableSignerItemHandler)
      },
      accepts: function (el, target, source, sibling) {
        return target !== source
      },
    })

    this.dragulaService
      .dropModel<FormGroup>(this.draggableSignerItemsGroupKey)
      .pipe(untilDestroy(this), debounceTime(100))
      .subscribe(({ target, targetModel: targetCtrls }) => {
        const roleOrder = lodash.toNumber(lodash.get(target, 'dataset.roleOrder', '1'))
        lodash.forEach(targetCtrls, (form) => {
          if (form.get('order').value !== roleOrder) {
            form.patchValue({ order: roleOrder }, { emitEvent: false })
          }
        })
        this.refreshOrder()
      })

    this.dragulaService
      .over(this.draggableSignerItemsGroupKey)
      .pipe(untilDestroy(this))
      .subscribe(({ container }) => {
        container.classList.add('dragover')
      })

    this.dragulaService
      .out(this.draggableSignerItemsGroupKey)
      .pipe(untilDestroy(this))
      .subscribe(({ container }) => {
        container.classList.remove('dragover')
      })

    this.itemsFormArray.valueChanges
      .pipe(startWith(this.itemsFormArray.getRawValue()), untilDestroy(this), pairwise())
      .subscribe(([data1, data2]) => {
        this.isSignerDataChanged = new Date() // update diagram
        if (data1.length !== data2.length) {
          this.refreshOrder()
        }
      })

    this.refreshOrder()
  }

  ngOnDestroy(): void {
    this.dragulaService.destroy(this.draggableSignerItemsGroupKey)
  }

  onMoveUp(item: FormGroup, groupId: number) {
    const sourceGroup = this.groupedSignerItemCtrls[groupId - 1]
    if (sourceGroup.length > 1) {
      item.patchValue({ order: groupId - 0.5 }, { emitEvent: false })
    } else {
      item.patchValue({ order: groupId - 1 }, { emitEvent: false })
    }
    this.refreshOrder()
  }

  onMoveDown(item: FormGroup, groupId: number) {
    const sourceGroup = this.groupedSignerItemCtrls[groupId - 1]
    if (sourceGroup.length > 1) {
      item.patchValue({ order: groupId + 0.5 }, { emitEvent: false })
    } else {
      item.patchValue({ order: groupId + 1 }, { emitEvent: false })
    }
    this.refreshOrder()
  }

  getGroupedCtrls() {
    let ctrls = this.itemsFormArray.controls as FormGroup[]
    const groups = lodash.groupBy(ctrls, (ctrl: FormGroup) => ctrl.get('order').value)
    const orderGroupPairs: [string, FormGroup[]][] = lodash.map(groups, (group, order) => [order, group])
    return lodash
      .sortBy(orderGroupPairs, ([order]) => {
        return lodash.toNumber(order) || orderGroupPairs.length // default to last group
      })
      .map(([order, group]) => group)
  }

  refreshOrder() {
    const ctrls = this.getGroupedCtrls()
    // reset order
    lodash.forEach(ctrls, (items, index) => {
      items.forEach((ctrl) => ctrl.patchValue({ order: index + 1 }, { emitEvent: false }))
    })
    this.groupedSignerItemCtrls = ctrls
    // reset form items order
    this.itemsFormArray.clear({ emitEvent: false })
    lodash.flatten(ctrls).forEach((ctrl) => this.itemsFormArray.push(ctrl, { emitEvent: false }))
  }
}
